import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { FaMoneyBillWave, FaArrowDown, FaArrowUp, FaBalanceScale, FaHistory, FaTags } from "react-icons/fa";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend);

const formatoMoneda = (valor) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    valor || 0
  );

// Etiquetas del eje vertical: solo son referencia de escala, sin centavos.
const formatoEje = (valor) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(valor || 0);

const formatoMes = (mes) => {
  if (!mes) return "";
  const [anio, mm] = mes.split("-");
  const fecha = new Date(Number(anio), Number(mm) - 1, 1);
  return fecha.toLocaleDateString("es-MX", { month: "short" });
};

export default function EstadisticasTesoreria({ tipo, titulo }) {
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(`/api/salidas/resumen?tipo=${tipo}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Error obteniendo resumen");
        setResumen(await res.json());
      } catch (err) {
        console.error("Error cargando resumen de tesorería:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [tipo]);

  if (loading) {
    return <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-72 animate-pulse" />;
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <p className="text-sm text-slate-400">No se pudo cargar la información de {titulo.toLowerCase()}.</p>
      </div>
    );
  }

  const tendencia = resumen?.tendencia || [];
  const porCategoria = resumen?.porCategoria || [];
  const mayorCategoria = Math.max(...porCategoria.map((c) => c.total), 1);
  const sinDatos =
    !resumen ||
    (resumen.ingresoAnio === 0 && resumen.salidaAnio === 0 && tendencia.every((t) => t.ingresos === 0 && t.salidas === 0));

  const data = {
    labels: tendencia.map((t) => formatoMes(t.mes)),
    datasets: [
      {
        label: "Ingresos",
        data: tendencia.map((t) => t.ingresos),
        borderColor: "#059669",
        backgroundColor: "rgba(5,150,105,0.10)",
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: "#059669",
      },
      {
        label: "Salidas",
        data: tendencia.map((t) => t.salidas),
        borderColor: "#4f46e5",
        backgroundColor: "rgba(79,70,229,0.12)",
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: "#4f46e5",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, color: "#64748b", font: { size: 11 } },
      },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatoMoneda(ctx.parsed.y)}` },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#94a3b8" } },
      y: { grid: { color: "#f1f5f9" }, ticks: { color: "#94a3b8", callback: (v) => formatoEje(v) } },
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <FaMoneyBillWave />
        </span>
        <h2 className="text-base font-semibold text-slate-800">{titulo}</h2>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
            <FaArrowDown className="text-[10px] text-emerald-600" /> Ingreso anual
          </p>
          <p className="text-base font-bold text-slate-800 truncate">{formatoMoneda(resumen?.ingresoAnio)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
            <FaArrowUp className="text-[10px] text-indigo-600" /> Salida anual
          </p>
          <p className="text-base font-bold text-slate-800 truncate">{formatoMoneda(resumen?.salidaAnio)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" title="Año anterior + ingreso anual − salida anual">
          <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
            <FaBalanceScale className="text-[10px]" /> Restante
          </p>
          <p className={`text-base font-bold truncate ${(resumen?.restante ?? 0) < 0 ? "text-rose-600" : "text-indigo-600"}`}>
            {formatoMoneda(resumen?.restante)}
          </p>
        </div>
      </div>

      {sinDatos ? (
        <p className="text-sm text-slate-400 text-center py-10">Aún no hay movimientos registrados.</p>
      ) : (
        <div className="h-48">
          <Line data={data} options={options} />
        </div>
      )}

      <div
        className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 text-sm"
        title="Saldo con el que cerró el año anterior; es el punto de partida del año en curso"
      >
        <span className="flex items-center gap-2 text-slate-500">
          <FaHistory className="text-xs" />
          Año anterior
          {resumen?.anioAnterior && <span className="text-slate-400">· cierre {resumen.anioAnterior}</span>}
        </span>
        <span className={`font-semibold ${(resumen?.saldoAnterior ?? 0) < 0 ? "text-rose-600" : "text-slate-800"}`}>
          {formatoMoneda(resumen?.saldoAnterior)}
        </span>
      </div>

      {porCategoria.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="flex items-center gap-2 text-sm text-slate-500 mb-3">
            <FaTags className="text-xs" /> Salidas del año por categoría
          </p>
          <ul className="space-y-2.5">
            {porCategoria.slice(0, 6).map((c) => (
              <li key={c.categoria}>
                <div className="flex items-baseline justify-between gap-3 text-xs mb-1">
                  <span className="text-slate-600 truncate">{c.categoria}</span>
                  <span className="font-semibold text-slate-800 shrink-0">{formatoMoneda(c.total)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500"
                    style={{ width: `${Math.max((c.total / mayorCategoria) * 100, 2)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
